/**
 * `GET /api/pitched`, `PUT /api/characters/{id}/pitched/{slug}` — per-character Pitched/Brilliant
 * Boss Set item ownership (REV6 — docs/specs/REV6-pitched-tracker.md). See `PitchedDtos` /
 * `PitchedController` / `PitchedService` for the authoritative shapes. Row-exists-means-obtained
 * toggle semantics, same idiom as `lib/bosses.ts`'s hidden-boss toggle.
 *
 * `PUT /api/characters/{id}/pitched-drops/{bossSlug}` (REV17 — docs/specs/REV17-pitched-drop-
 * counters.md) rides the same `GET /api/pitched` response (`dropCounts`) but is fully independent
 * of the obtained toggle above — see `useSetPitchedDropCount` below.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiGet, apiSend, ApiError } from './api';
import { pushToast } from './events';

/** REV17 — a character's lifetime drop tally for one countable boss. */
export interface PitchedDropCountDto {
  bossSlug: string;
  count: number;
}

export interface CharacterPitchedDto {
  characterId: number;
  obtainedSlugs: string[];
  /** REV17 — every countable boss this character has ever touched; an absent boss slug is 0. */
  dropCounts: PitchedDropCountDto[];
}

export interface PitchedAccountResponse {
  accountId: number;
  characters: CharacterPitchedDto[];
}

export function pitchedForAccountQuery(accountId: number) {
  return {
    queryKey: ['pitched', accountId] as const,
    queryFn: ({ signal }: { signal: AbortSignal }) =>
      apiGet<PitchedAccountResponse>(`/api/pitched?accountId=${accountId}`, signal),
  };
}

export function usePitchedForAccount(accountId: number | null) {
  return useQuery({
    ...pitchedForAccountQuery(accountId ?? -1),
    enabled: accountId !== null,
  });
}

export interface SetPitchedObtainedVariables {
  accountId: number;
  characterId: number;
  slug: string;
  obtained: boolean;
}

/** Replaces one character's `obtainedSlugs` in a cached account-wide response. */
function withObtainedSet(
  data: PitchedAccountResponse,
  characterId: number,
  slug: string,
  obtained: boolean,
): PitchedAccountResponse {
  return {
    ...data,
    characters: data.characters.map((character) => {
      if (character.characterId !== characterId) {
        return character;
      }
      const withoutThisOne = character.obtainedSlugs.filter((s) => s !== slug);
      return {
        ...character,
        obtainedSlugs: obtained ? [...withoutThisOne, slug] : withoutThisOne,
      };
    }),
  };
}

/**
 * `PUT /api/characters/{id}/pitched/{slug}` — optimistic apply + rollback on error, same shape as
 * `useHideBoss`/`useUnhideBoss`.
 */
export function useSetPitchedObtained() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (vars: SetPitchedObtainedVariables) =>
      apiSend('PUT', `/api/characters/${vars.characterId}/pitched/${vars.slug}`, {
        obtained: vars.obtained,
      }),
    onMutate: async (vars) => {
      const key = pitchedForAccountQuery(vars.accountId).queryKey;
      await queryClient.cancelQueries({ queryKey: key });
      const previous = queryClient.getQueryData<PitchedAccountResponse>(key);
      if (previous) {
        queryClient.setQueryData<PitchedAccountResponse>(
          key,
          withObtainedSet(previous, vars.characterId, vars.slug, vars.obtained),
        );
      }
      return { previous, key };
    },
    onError: (error, _vars, context) => {
      if (context) {
        queryClient.setQueryData(context.key, context.previous);
      }
      const message = error instanceof ApiError ? error.serverMessage : 'Could not save that item.';
      pushToast(`${message} Your change was rolled back — try again.`);
    },
    onSettled: (_data, _error, vars) =>
      void queryClient.invalidateQueries({ queryKey: pitchedForAccountQuery(vars.accountId).queryKey }),
  });
}

export interface SetPitchedDropCountVariables {
  accountId: number;
  characterId: number;
  bossSlug: string;
  count: number;
}

/** Replaces one character's drop count for `bossSlug` in a cached account-wide response. */
function withDropCountSet(
  data: PitchedAccountResponse,
  characterId: number,
  bossSlug: string,
  count: number,
): PitchedAccountResponse {
  return {
    ...data,
    characters: data.characters.map((character) => {
      if (character.characterId !== characterId) {
        return character;
      }
      const withoutThisOne = character.dropCounts.filter((d) => d.bossSlug !== bossSlug);
      return {
        ...character,
        dropCounts: count > 0 ? [...withoutThisOne, { bossSlug, count }] : withoutThisOne,
      };
    }),
  };
}

/**
 * `PUT /api/characters/{id}/pitched-drops/{bossSlug}` (REV17) — an absolute set, same
 * optimistic-apply + rollback shape as {@link useSetPitchedObtained}. Steppers compute the next
 * value client-side and send it whole, not a delta.
 */
export function useSetPitchedDropCount() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (vars: SetPitchedDropCountVariables) =>
      apiSend('PUT', `/api/characters/${vars.characterId}/pitched-drops/${vars.bossSlug}`, {
        count: vars.count,
      }),
    onMutate: async (vars) => {
      const key = pitchedForAccountQuery(vars.accountId).queryKey;
      await queryClient.cancelQueries({ queryKey: key });
      const previous = queryClient.getQueryData<PitchedAccountResponse>(key);
      if (previous) {
        queryClient.setQueryData<PitchedAccountResponse>(
          key,
          withDropCountSet(previous, vars.characterId, vars.bossSlug, vars.count),
        );
      }
      return { previous, key };
    },
    onError: (error, _vars, context) => {
      if (context) {
        queryClient.setQueryData(context.key, context.previous);
      }
      const message = error instanceof ApiError ? error.serverMessage : 'Could not save that drop count.';
      pushToast(`${message} Your change was rolled back — try again.`);
    },
    onSettled: (_data, _error, vars) =>
      void queryClient.invalidateQueries({ queryKey: pitchedForAccountQuery(vars.accountId).queryKey }),
  });
}
